import { useState, useEffect } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faPlus } from "@fortawesome/free-solid-svg-icons";

import Table from "components/Common/Table/Table";
import SearchOptions from "components/Common/SearchOptions/SearchOptions";
import ButtonSmall from "components/Common/ButtonSmall/ButtonSmall";
import TableCell from "components/Common/TableCell/TableCell";
import PageTitle from "components/Common/PageTitle/PageTitle";
import * as Database from "utils/database.utils";

import CreateCompanyModal from "./CreateCompanyModal/CreateCompanyModal";
import CompanyDetailsModal from "./CompanyDetailsModal/CompanyDetailsModal";

import CompanyManagementContainer from "./CompanyManagement.styles";
import { SelectOption } from "./CompanyForm";

export default function CompanyManagement() {
    const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState<any>(null);
    const [filterQuery, setFilterQuery] = useState<string>("");
    const [reloadCompanies, setReloadCompanies] = useState(0);
    const [teamOptions, setTeamOptions] = useState<SelectOption[]>([]);

    useEffect(() => {
        const fetchTeams = async () => {
            try {
                const res = await Database.get(
                    "api/company/teams?usedInCompanyManagement=true"
                );
                if (res.ok) {
                    const data = await res.json();
                    setTeamOptions(
                        data.data.map((item: { id: number; name: string }) => ({
                            label: item.name,
                            value: String(item.id),
                        }))
                    );
                }
            } catch (err) {
                console.error("Error fetching teams:", err);
            }
        };
        fetchTeams();
    }, []);

    const columns = [
        {
            id: 1,
            title: "COMPANY",
            sortable: true,
            value: "name",
            cell: (data: string) => <TableCell data={data} />,
            show: true,
        },
        {
            id: 2,
            title: "TEAM",
            sortable: true,
            value: "team_name",
            cell: (data: string) => <TableCell data={data ?? "-"} />,
            show: true,
        },
        {
            id: 3,
            title: "ANALYST",
            sortable: true,
            value: "analyst_name",
            cell: (data: string) => <TableCell data={data} />,
            show: true,
        },
        {
            id: 4,
            title: "GEF FL",
            sortable: true,
            value: "gef_focus_list",
            cell: (data: boolean) =>
                data ? (
                    <FontAwesomeIcon
                        className="checkIcon"
                        icon={faCheckCircle}
                        fixedWidth
                    />
                ) : null,
            show: true,
        },
        {
            id: 5,
            title: "AEF FL",
            sortable: true,
            value: "aef_focus_list",
            cell: (data: boolean) =>
                data ? (
                    <FontAwesomeIcon
                        className="checkIcon"
                        icon={faCheckCircle}
                        fixedWidth
                    />
                ) : null,
            show: true,
        },
        {
            id: 6,
            title: "TICKER",
            sortable: true,
            value: "ticker",
            cell: (data: string) => <TableCell data={data} />,
            show: true,
        },
        {
            id: 7,
            title: "ISIN",
            sortable: true,
            value: "isin",
            cell: (data: string) => <TableCell data={data} />,
            show: true,
        },
        {
            id: 8,
            title: "SEDOL ID",
            sortable: true,
            value: "sedol",
            cell: (data: string) => <TableCell data={data} />,
            show: true,
        },
        {
            id: 9,
            title: "CUSIP ID",
            sortable: true,
            value: "cusip",
            cell: (data: string) => <TableCell data={data} />,
            show: true,
        },
    ];

    const filterOptions = [
        { id: 1, name: "Search COMPANY", value: "name", type: "SEARCH" },
        {
            id: 2,
            name: "Team",
            value: "team_id",
            type: "DROPDOWN",
            options: teamOptions,
        },
        {
            id: 3,
            name: "GEF FL",
            value: "gef_focus_list",
            type: "SWITCH",
        },
        {
            id: 4,
            name: "AEF FL",
            value: "aef_focus_list",
            type: "SWITCH",
        },
    ];

    return (
        <CompanyManagementContainer>
            <PageTitle title="COMPANIES" />

            <SearchOptions
                setFilterQuery={setFilterQuery}
                options={filterOptions}
                actions={
                    <ButtonSmall
                        text={"CREATE COMPANY"}
                        icon={faPlus}
                        click={() => setShowCreateCompanyModal(true)}
                    />
                }
            />

            <Table
                url={"api/company"}
                columns={columns}
                filterQuery={filterQuery}
                defaultSortKey="name"
                pagination
                emptyText="NO COMPANIES"
                reload={reloadCompanies}
                onRowClick={(row: any) => setSelectedCompany(row)}
            />

            {showCreateCompanyModal && (
                <CreateCompanyModal
                    setShowModal={setShowCreateCompanyModal}
                    reloadCompanies={() =>
                        setReloadCompanies(reloadCompanies + 1)
                    }
                />
            )}

            {selectedCompany && (
                <CompanyDetailsModal
                    company={selectedCompany}
                    setSelectedCompany={setSelectedCompany}
                    reloadCompanies={() =>
                        setReloadCompanies(reloadCompanies + 1)
                    }
                />
            )}
        </CompanyManagementContainer>
    );
}
